import React, { forwardRef } from 'react';
import { Line } from 'react-chartjs-2';
import { useTheme } from '../ThemeContext';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ChartDataLabels);

interface RevenueExpensesChartProps {
  labels: string[];
  revenue: number[];
  expenses: number[];
}

const RevenueExpensesChart = forwardRef<any, RevenueExpensesChartProps>(({ labels, revenue, expenses }, ref) => {
  const { chart } = useTheme();

  const data = {
    labels,
    datasets: [
      {
        label: 'Revenue (Pendapatan)',
        data: revenue,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.08)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointBackgroundColor: '#10b981',
        pointBorderColor: chart.pointBorder,
        pointBorderWidth: 2,
        borderWidth: 2.5,
      },
      {
        label: 'Expenses (Pengeluaran)',
        data: expenses.map(v => Math.abs(v)),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.06)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointBackgroundColor: '#ef4444',
        pointBorderColor: chart.pointBorder,
        pointBorderWidth: 2,
        borderWidth: 2.5,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: chart.legendColor,
          font: { family: 'Inter', size: 12, weight: '500' },
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
        },
      },
      tooltip: {
        backgroundColor: chart.tooltipBg,
        titleColor: chart.tooltipTitle,
        bodyColor: chart.tooltipBody,
        borderColor: chart.tooltipBorder,
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        titleFont: { family: 'Inter', size: 13, weight: '600' },
        bodyFont: { family: 'Inter', size: 12 },
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.parsed.y;
            return `${ctx.dataset.label}: Rp ${(val / 1e6).toFixed(1)} Jt`;
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        offset: 8,
        font: {
          family: 'Inter',
          size: 11,
          weight: '500',
        },
        color: (context: any) => context.dataset.borderColor,
        formatter: (value: any) => {
          if (value === 0 || value === null) return '';
          return `Rp ${(value / 1e6).toFixed(1)} Jt`;
        },
      },
    },
    scales: {
      x: {
        ticks: { color: chart.tickColor, font: { family: 'Inter', size: 11 } },
        grid: { color: chart.gridColorX },
      },
      y: {
        ticks: {
          color: chart.tickColor,
          font: { family: 'Inter', size: 11 },
          callback: (val: number) => `Rp ${(val / 1e6).toFixed(0)}Jt`,
        },
        grid: { color: chart.gridColorY },
      },
    },
  };

  return (
    <div className="chart-card animate-in" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="chart-card-header">
        <div>
          <div className="chart-card-title">📈 Revenue vs Expenses Trend</div>
          <div className="chart-card-subtitle">Perbandingan pemasukan dan pengeluaran bulanan</div>
        </div>
      </div>
      <div className="chart-wrapper line-chart">
        <Line ref={ref} data={data} options={options} />
      </div>
    </div>
  );
});

RevenueExpensesChart.displayName = 'RevenueExpensesChart';

export default RevenueExpensesChart;
